import React, { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Spinner from 'components/Spinner'
import Table from 'components/Table'

import CustomerDeviceList from 'pages/shared/CustomerList/CustomerDeviceList'
import PiiFlag from 'pages/Customers/PiiFlag'
import Pin from 'pages/shared/Pin'

import useSearchParams from 'hooks/useSearchParams'
import { useStoreContext } from 'hooks/StoreContext'

import { streetAddress, cityStateZip, customerDisplayName } from 'helpers/formatters'

import { color } from 'styles/colors'
import { screenSize, NoData } from 'styles/shared'
import Apptentive from 'helpers/apptentive'

const ASC_SORT = 'asc'
const DESC_SORT = 'desc'

type noDataMessage = {
  title: string;
  description: string | null;
}

type Props = {
  noDataMessage: noDataMessage;
  constructUrlCallback: (params) => string;
  updateRefreshTimestampCallback: () => void;
  defaultSortAttribute?: string;
  defaultSortDirection?: string;
}

// TODO: This and other components in this folder should not be under the shared directory, it's unless used from the Customers page
const CustomersList = ({ noDataMessage, constructUrlCallback, updateRefreshTimestampCallback, defaultSortAttribute = 'customer', defaultSortDirection = ASC_SORT }: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const sortAttributeFromUrl = useSearchParams('sortAttribute') || defaultSortAttribute
  const sortDirectionFromUrl = useSearchParams('sortDirection') || defaultSortDirection
  const { customers, arePagedCustomersLoaded } = useStoreContext()

  const [hoveredCustomerId, setHoveredCustomerId] = useState(null)

  const variantType = (customer) => {
    if (customer.piiNew) return 'new'
    else if (customer.piiUpdated) return 'updated'
    else if (customer.piiDuplicate) return 'duplicate'
    else return null
  }

  const navigateToSortedList = (newAttribute) => {
    let newSortDirection

    if (sortAttributeFromUrl === newAttribute) {
      newSortDirection = sortDirectionFromUrl === ASC_SORT ? DESC_SORT : ASC_SORT
    } else {
      newSortDirection = ASC_SORT
    }

    navigate(constructUrlCallback({ sortAttribute: newAttribute, sortDirection: newSortDirection }))
  }

  const sortDirectionForAttribute = (attribute) => {
    return (sortAttributeFromUrl === attribute) ? (sortDirectionFromUrl === ASC_SORT ? DESC_SORT : ASC_SORT) : null
  }

  const sendEvent = () => {
    const eventName = location.pathname.includes('/dashboard') ? 'view_customer_from_dashboard_alerts' : 'view_customer_from_customers'
    Apptentive.sendEvent(eventName)
  }

  return (
    <Table
      items={customers}
    >
      <Table.Header sticky>
        <S.HeaderCell title="Customer" sortable onSort={() => { navigateToSortedList('customer') }} sortDirection={sortDirectionForAttribute('customer')} />
        <S.HeaderCell title="Device" />
        <S.HeaderCell title="Model" />
        <S.HeaderCell title="Alerts" sortable onSort={() => { navigateToSortedList('alerts') }} sortDirection={sortDirectionForAttribute('alerts')} />
        <S.HeaderCell title="Date Added" sortable onSort={() => { navigateToSortedList('created_at') }} sortDirection={sortDirectionForAttribute('created_at')} />
        <S.HeaderCell title="Action Taken" />
      </Table.Header>

      {
        (() => {
          if (!arePagedCustomersLoaded) {
            return <Spinner variant="page" />
          } else if (customers?.length === 0) {
            return (
              <NoData>
                <h1>{noDataMessage.title}</h1>
                {noDataMessage.description && <h2>{noDataMessage.description}</h2>}
              </NoData>
            )
          } else {
            return (
              customers?.map((customer) => (
                <Table.Row key={customer.id} onMouseEnter={() => { setHoveredCustomerId(customer.id) }} onMouseLeave={() => { setHoveredCustomerId(null) }}>
                  <S.Cell>
                    <S.CustomerInfo>
                      <S.TopRow>
                        <S.Pin
                          isPinned={customer.pinned}
                          pinnedAt={customer.pinnedAt}
                          displayUnpinned={hoveredCustomerId === customer.id}
                          customerName={customerDisplayName(customer.firstName, customer.lastName, customer.companyName)} customerId={customer.id}
                        />
                        <S.CustomerNameLink to={`/customers/${customer.id}`} onClick={sendEvent}>
                          {customerDisplayName(customer.firstName, customer.lastName, customer.companyName)}
                        </S.CustomerNameLink>
                      </S.TopRow>

                      <S.BodyRow>
                        {variantType(customer) && <PiiFlag customerId={customer.id} variant={variantType(customer)} duplicateNames={customer.piiHomeDuplicateNames} />}
                        <div>{streetAddress(customer.address1, customer.address2)}</div>
                        <div>{cityStateZip(customer.city, customer.state, customer.zip)}</div>
                      </S.BodyRow>
                    </S.CustomerInfo>
                  </S.Cell>
                  <CustomerDeviceList
                    customer={customer}
                    updateRefreshTimestampCallback={updateRefreshTimestampCallback}
                  />
                </Table.Row>
              ))
            )
          }
        })()
      }
    </Table>
  )
}

export default CustomersList

const S = {
  Pin: styled(Pin)`
    padding: 3px 8px;
    margin-top: 3px;

    @media ${screenSize.small} {
      padding: 0 6px 0 0;
    }
  `,
  Cell: styled(Table.Cell)`
    overflow: hidden;
    flex: 1;
    padding: 20px 0;
    min-width: 113px; ${''/* this matches the customer cell header minimum width */}

    @media ${screenSize.small} {
      border-top: 1px solid ${color('parentDivider')};
      border-bottom: 1px solid ${color('divider')};
    }
  `,
  CustomerInfo: styled.div`
    display: flex;
    flex-direction: column;
  `,
  HeaderCell: styled(Table.HeaderCell)`
    padding-right: 30px;
  `,
  TopRow: styled.div`
    display: grid;
    grid-template-columns: 30px 1fr 26px;
  `,
  BodyRow: styled.div`
    padding-left: 30px;
  `,
  CustomerNameLink: styled(NavLink)`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `
}
